import {
  ChartColorContext,
  ChartDisclaimer,
  ChartErrorComponent,
  defaultEChartsOptionForInterval,
  ECHARTS_CONTAINER_HEIGHT as height,
  nowMarkLineSeriesOption,
} from '@e2m-customer-portal/domains';
import { useLocale, useSpotPrices } from '@e2m-customer-portal/hooks';
import { EChartsOption, EChartsType, SeriesOption } from 'echarts';
import { Interval } from 'generated/graphql';
import { ReactElement, useContext, useRef } from 'react';
import EChartsReact, { EChartsReactRef } from 'react-echarts-library';
import { useTranslation } from 'react-i18next';
import { ChartColor } from './utils';
import { Button } from '@mui/material';
import { ZoomOutIcon } from 'assets/icons';
import { addMinutes, getTime } from 'date-fns';

interface MarketPriceChartProps {
  dataInterval: Interval;
  onChartReadyCallback: (instance: EChartsType) => void;
  resetZoom: (ref: EChartsReactRef) => void;
  handleEvents: any;
  isZoomed?: boolean;
}

export function MarketPriceChart({
  dataInterval,
  onChartReadyCallback,
  resetZoom,
  handleEvents,
  isZoomed,
}: Readonly<MarketPriceChartProps>): ReactElement<MarketPriceChartProps> {
  const [t] = useTranslation();
  const { code: locale } = useLocale();
  const chartRef = useRef<EChartsReactRef>(null);
  const chartColorVariant = useContext(ChartColorContext);
  const { data: spotPriceData, error } = useSpotPrices({ interval: dataInterval });
  const dayAheadSpotPrice = spotPriceData?.dayAheadForecasts
    .flatMap((slice) => [
      [getTime(slice.date), slice.price],
      [getTime(addMinutes(slice.date, 15)), slice.price],
      [getTime(addMinutes(slice.date, 30)), slice.price],
      [getTime(addMinutes(slice.date, 45)), slice.price],
    ])
    .sort(([a], [b]) => a - b);

  const intradaySpotPrice = spotPriceData?.intradayForecasts
    .flatMap((slice) => [
      [getTime(slice.date), slice.price],
      [getTime(addMinutes(slice.date, 15)), slice.price],
      [getTime(addMinutes(slice.date, 30)), slice.price],
      [getTime(addMinutes(slice.date, 45)), slice.price],
    ])
    .sort(([a], [b]) => a - b);

  const spotPriceCutOffTimeStamp = spotPriceData?.latestHistoricalSpotPrice?.getTime() ?? dataInterval.start.getTime();

  const historicalSeriesData = dayAheadSpotPrice?.filter(([timestamp]) => timestamp <= spotPriceCutOffTimeStamp);
  const forecastedSeriesData = dayAheadSpotPrice?.filter(([timestamp]) => timestamp > spotPriceCutOffTimeStamp);

  const spotPriceSeries = {
    name: t('MARKET_LOCATION_FEED_IN_CHART_SPOT_PRICE'),
    type: 'line',
    step: 'end',
    color: ChartColor.PrimaryFull[chartColorVariant],
    symbol: 'circle',
    showSymbol: false,
  } satisfies SeriesOption;

  const option: EChartsOption = {
    ...defaultEChartsOptionForInterval(t, dataInterval),
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: '{value} €/MWh',
        color: '#888888',
      },
      splitLine: {
        lineStyle: {
          color: '#F0F0F0',
        },
      },
    },
    series: [
      {
        ...spotPriceSeries,
        data: historicalSeriesData,
        tooltip: {
          valueFormatter: (value) =>
            value == null
              ? '-'
              : `${(Math.round(Number(value) * 100) / 100).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/MWh`,
        },
      },
      {
        ...spotPriceSeries,
        data: [historicalSeriesData?.at(-1), forecastedSeriesData?.at(0)],
        tooltip: {
          show: false,
        },
      },
      {
        ...spotPriceSeries,
        data: forecastedSeriesData,
        lineStyle: {
          type: 'dotted',
        },
        tooltip: {
          valueFormatter: (value) =>
            value == null
              ? '-'
              : `${(Math.round(Number(value) * 100) / 100).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/MWh *`,
        },
      },
      {
        name: t('MARKET_LOCATION_FEED_IN_CHART_SPOT_PRICE_INTRADAY'),
        data: intradaySpotPrice,
        type: 'line',
        step: 'end',
        color: ChartColor.SecondaryFull[chartColorVariant],
        symbol: 'circle',
        showSymbol: false,
        tooltip: {
          valueFormatter: (value) =>
            value == null
              ? '-'
              : `${(Math.round(Number(value) * 100) / 100).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/MWh`,
        },
      },
      nowMarkLineSeriesOption,
    ],
  };

  return (
    <div className="relative">
      {isZoomed && (
        <Button
          className="absolute top-8 left-32 w-fit px-1 bg-white/45 z-40"
          style={{ color: ChartColor.PrimaryFull[chartColorVariant] }}
          color="inherit"
          variant="outlined"
          onClick={() => chartRef.current && resetZoom(chartRef.current)}
        >
          <ZoomOutIcon style={{ fill: ChartColor.PrimaryFull[chartColorVariant] }} />
        </Button>
      )}
      <EChartsReact
        style={{ height }}
        option={option}
        opts={{ locale, renderer: 'svg' }}
        data-testid="market-price-chart"
        onChartReady={onChartReadyCallback}
        onEvents={handleEvents}
        ref={chartRef}
      />
      {error && <ChartErrorComponent errors={[error]} />}
      <ChartDisclaimer
        title={t('COLOCATION_OVERVIEW_CHARTS_MARKET_PRICES_DISCLAIMER_TITLE')}
        text={t('COLOCATION_OVERVIEW_CHARTS_MARKET_PRICES_DISCLAIMER')}
      />
    </div>
  );
}
